import Loadable from 'react-loadable';

import { packagesAvailabilityResource } from 'src/components/packages/resources/packages-availability-resource';
import { packagesResource } from 'src/components/packages/resources/packages-resource';
import { loadThirdPartySecretsProviderResource } from 'src/components/pipelines/components/third-party-secrets/components/secret-provider-endpoints';
import { loadAgenticPipelinesConfiguration } from 'src/components/pipelines/resources/agentic-pipelines-configuration';
import { loadAllowanceResource } from 'src/components/pipelines/resources/allowance';
import { loadWorkspaceDynamicPipelinesResource } from 'src/components/pipelines/resources/dynamic-pipelines';
import { loadPipelineUsageDetailsResource } from 'src/components/pipelines/resources/pipelines-usage-details';
import { loadPipelinesWorkspaceRunnersResource } from 'src/components/pipelines/resources/runners';
import { loadRunnersStepQueueResource } from 'src/components/pipelines/resources/runners-step-queue';
import { loadRunnersStepQueueStatsResource } from 'src/components/pipelines/resources/runners-step-queue-stats';
import { loadWorkspaceAllowanceResource } from 'src/components/pipelines/resources/workspace-allowance';
import { loadWorkspaceLinkedResource } from 'src/components/pipelines/resources/workspace-linked';
import { loadWorkspacePipelinesResource } from 'src/components/pipelines/resources/workspace-pipelines';
import { loadPipelinesWorkspaceVariablesResource } from 'src/components/pipelines/resources/workspace-variables';
import { LayoutLoadingPage, LoadingPage } from 'src/components/react-loadable';
import { jiraSitesResource } from 'src/components/rovo-dev-agents/src/resources/jira-sites-resource';
import { customMergeChecksConfigRouteResource } from 'src/components/settings/merge-checks/resources';
import { loadGlobalResource } from 'src/redux/global/resources/load-global';
import { dormantWorkspacePartOfWsCleanupResource } from 'src/sections/account/dormant-workspace-part-of-ws-cleanup-resource';
import { dashboardOverviewViewResource } from 'src/sections/dashboard/dashboard-overview-view-resource';
import { workspaceRepositoriesResource } from 'src/sections/profile/sections/repositories/workspace-repositories-resource';
import { workspaceSearchResource } from 'src/sections/search/workspace-search-resource';
import { forgeWorkspaceNavigationResource } from 'src/sections/workspace/forge-workspace-navigation-resource';
import { workspaceProjectsResource } from 'src/sections/workspace/sections/projects/workspace-projects-resource';
import { workspacePullRequestsResource } from 'src/sections/workspace/sections/pullrequests/workspace-pull-requests-resource';
import { WorkspaceSettingsAccessTokensLoadable } from 'src/sections/workspace/sections/settings/components/access-tokens';
import { compassResource } from 'src/sections/workspace/sections/settings/components/compass/compass-resource';
import { immutableTagSettingsResource } from 'src/sections/workspace/sections/settings/components/container/resources/immutable-tag-settings-resource';
import { WorkspaceSettingsOAuthLoadable } from 'src/sections/workspace/sections/settings/components/oauth';
import { oauthClientsResource } from 'src/sections/workspace/sections/settings/components/oauth/resources/oauth-clients-resource';
import { WorkspaceSettingsPermissionsLoadable } from 'src/sections/workspace/sections/settings/components/permissions';
import { loadWorkspaceSettingsPermissionsResource } from 'src/sections/workspace/sections/settings/components/permissions/resources';
import PipelinesWorkspaceAgenticPipelinesPageLoadable from 'src/sections/workspace/sections/settings/components/pipelines/agentic-pipelines';
import PipelinesWorkspaceDynamicPipelinesPageLoadable from 'src/sections/workspace/sections/settings/components/pipelines/dynamic-pipelines';
import PipelinesWorkspaceUsageDetailsPageLoadable from 'src/sections/workspace/sections/settings/components/pipelines/pipelines-usage-details';
import PipelinesWorkspaceRunnersPageLoadable from 'src/sections/workspace/sections/settings/components/pipelines/runners';
import PipelinesWorkspaceVariablesPageLoadable from 'src/sections/workspace/sections/settings/components/pipelines/variables';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';
import { workspaceSectionResource } from 'src/sections/workspace/workspace-section-resource';

import { Route, ROUTE_NAME, RouteContext, RouteFeatures } from './types';

const RootRedirect = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/global/containers/root-redirect'),
});

const WorkspaceRedirect = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/workspace/workspace-redirect'),
});

const DashboardOverview = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/dashboard/overview/containers'),
});

const FocusedTaskPageLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () =>
    import('src/sections/global/components/focused-task-page-layout'),
});

const WorkspaceLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () => import('src/sections/workspace/layout'),
});

const WorkspaceSettingsLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () => import('src/sections/workspace/settings-layout'),
});

const WorkspaceCreatePage = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/create/containers/create-workspace-page'
    ),
});

const WorkspacePullRequests = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/workspace/sections/pullrequests/route'),
});

const WorkspaceSearchRoute = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/search/workspace-search-route'),
});

const WorkspaceRepositories = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/profile/sections/repositories/route'),
});

const WorkspaceProjects = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/sections/projects/resource-router-route'),
});

const WorkspacePackagesCreate = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/sections/packages/packages-create-page'),
});

const PackagesPage = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/sections/packages/packages-page').then(
      m => m.WorkspacePackagesPage
    ),
});

const WorkspaceSettingsConnect = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/containers/settings-connect-iframe'
    ),
});

const WorkspaceSettingsForge = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/forge/workspace-settings-menu-page/workspace-settings-menu-page'
    ),
});

const WorkspaceGlobalForge = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/forge/workspace-global-menu-page/workspace-global-menu-page'),
});

const WorkspacePersonalSettingsForge = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/forge/workspace-personal-settings-menu-page/workspace-personal-settings-menu-page'
    ),
});

const WorkspaceIframe = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/containers/iframe-wrapped'
    ),
});

const WorkspaceSettingsGroup = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/sections/settings/components/group'),
});

const WorkspaceSettingsGroups = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/sections/settings/components/groups'),
});

const WorkspaceSettingsRoot = Loadable<any, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/settings-index'
    ),
});

const WorkspaceSettingsUserDirectory = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/user-directory'
    ),
});

const WorkspaceSettingsRovoDevAgents = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/rovo-dev-agents'
    ),
});

const WorkspaceSettingsAtlassianIntelligence = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/atlassian-intelligence'
    ),
});

const WorkspaceSettingsCompass = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/compass/compass-workspace-settings-page'
    ),
});

const WorkspaceSettingsPackagesContainer = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/container/container-settings-page'
    ),
});

const WorkspaceSettingsMergeChecks = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/merge-checks/merge-checks-workspace-settings-page'
    ),
});

const PipelinesWorkspaceSettingsRedirectSettingsLoadable = Loadable<
  RouteContext,
  never
>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/pipelines/redirect/to-workspace-settings'
    ),
});

const PipelinesWorkspaceSettingsRedirectRunnersLoadable = Loadable<
  RouteContext,
  never
>({
  loading: LoadingPage,
  loader: () =>
    import(
      'src/sections/workspace/sections/settings/components/pipelines/redirect/to-workspace-settings'
    ),
});

const ConnectPageLoadable = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/connect/profile-tab'),
});

const WorkspaceSideNavigationLoadable = Loadable<{}, never>({
  loading: LoadingPage,
  loader: () => import('src/app/nav-system/side-nav/workspace-admin'),
});

const WorkspaceDeactivatedReactivate = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/workspace/workspace-deactivated-reactivate/index'),
});

const defaultResources = [
  loadGlobalResource,
  workspaceSectionResource,
  workspaceNavigationResource,
  forgeWorkspaceNavigationResource,
];

export const workspaceRoutes = ({
  isIframeCreateRepoUIEnabled,
  isWorkspaceSettingsIndexEnabled,
  isAgenticPipelinesMenuEnabled,
  isPackagesImmutableTagsEnabled,
}: RouteFeatures): Route[] => {
  const routes: Route[] = [
    {
      name: ROUTE_NAME.ROOT,
      path: '/',
      exact: true,
      component: RootRedirect,
      resources: [],
    },
    {
      name: ROUTE_NAME.WORKSPACE_OVERVIEW,
      path: '/:workspaceSlug/workspace/overview',
      exact: true,
      component: DashboardOverview,
      layout: WorkspaceLayout,
      resources: [
        ...defaultResources,
        dashboardOverviewViewResource,
        jiraSitesResource,
        loadWorkspacePipelinesResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_CREATE,
      path: '/workspace/create',
      component: WorkspaceCreatePage,
      layout: FocusedTaskPageLayout,
      resources: [loadGlobalResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_ROOT_REDIRECT,
      path: '/:workspaceSlug',
      exact: true,
      component: WorkspaceRedirect,
      layout: WorkspaceLayout,
      resources: [loadGlobalResource],
    },
    {
      name: ROUTE_NAME.PROFILE_REPOSITORIES,
      path: '/:workspaceSlug/profile/repositories',
      exact: false,
      component: WorkspaceRepositories,
      layout: WorkspaceLayout,
      resources: [...defaultResources, workspaceRepositoriesResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_REPOSITORIES,
      path: '/:workspaceSlug/workspace/repositories',
      exact: false,
      component: WorkspaceRepositories,
      layout: WorkspaceLayout,
      resources: [...defaultResources, workspaceRepositoriesResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_DEACTIVATED_REACTIVATE,
      path: '/workspaces/:workspaceSlug/reactivate',
      exact: false,
      component: WorkspaceDeactivatedReactivate,
      layout: FocusedTaskPageLayout,
      resources: [loadGlobalResource, dormantWorkspacePartOfWsCleanupResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_IFRAME,
      path: '/:workspaceSlug/workspace/members',
      exact: true,
      component: WorkspaceIframe,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_IFRAME,
      path: '/:workspaceSlug/workspace/snippets',
      component: WorkspaceIframe,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_ROOT,
      path: '/:workspaceSlug/workspace/settings',
      exact: true,
      component: isWorkspaceSettingsIndexEnabled
        ? WorkspaceSettingsRoot
        : WorkspaceIframe,
      sideNavigation: WorkspaceSideNavigationLoadable,
      layout: WorkspaceSettingsLayout,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PERMISSIONS,
      path: '/:workspaceSlug/workspace/settings/permissions',
      exact: true,
      component: WorkspaceSettingsPermissionsLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [
        ...defaultResources,
        loadWorkspaceSettingsPermissionsResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_GROUP,
      path: '/:workspaceSlug/workspace/settings/groups/:groupSlug',
      exact: false,
      component: WorkspaceSettingsGroup,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_GROUPS,
      path: '/:workspaceSlug/workspace/settings/groups',
      exact: true,
      component: WorkspaceSettingsGroups,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_USER_DIRECTORY,
      path: '/:workspaceSlug/workspace/settings/user-directory',
      exact: false,
      component: WorkspaceSettingsUserDirectory,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_ATLASSIAN_INTELLIGENCE,
      path: '/:workspaceSlug/workspace/settings/atlassian-intelligence',
      exact: false,
      component: WorkspaceSettingsAtlassianIntelligence,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources, loadAgenticPipelinesConfiguration],
    },
    {
      // this is currently gated behind the feature flag workspace-admin-compass-pageß
      name: ROUTE_NAME.WORKSPACE_SETTINGS_COMPASS,
      path: '/:workspaceSlug/workspace/settings/compass',
      exact: false,
      component: WorkspaceSettingsCompass,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources, compassResource],
    },
    ...(isPackagesImmutableTagsEnabled
      ? [
          {
            name: ROUTE_NAME.WORKSPACE_SETTINGS_CONTAINER,
            path: '/:workspaceSlug/workspace/settings/packages/containers',
            exact: true,
            component: WorkspaceSettingsPackagesContainer,
            layout: WorkspaceSettingsLayout,
            sideNavigation: WorkspaceSideNavigationLoadable,
            resources: [...defaultResources, immutableTagSettingsResource],
          },
        ]
      : []),
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_MERGE_CHECKS,
      path: '/:workspaceSlug/workspace/settings/merge-checks',
      exact: false,
      component: WorkspaceSettingsMergeChecks,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources, customMergeChecksConfigRouteResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_REDIRECT_SETTINGS,
      path: '/:workspaceSlug/workspace/settings/addon/admin/:appKey(pipelines)/:moduleKey(account-variables)',
      exact: true,
      component: PipelinesWorkspaceSettingsRedirectSettingsLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_REDIRECT_RUNNERS,
      path: '/:workspaceSlug/workspace/settings/addon/admin/:appKey(pipelines)/:moduleKey(account-runners)',
      exact: true,
      component: PipelinesWorkspaceSettingsRedirectRunnersLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_CONNECT_ADMIN,
      path: '/:workspaceSlug/workspace/settings/addon/admin/:appKey/:moduleKey',
      exact: false,
      component: WorkspaceSettingsConnect,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_CONNECT_CONFIGURE,
      path: '/:workspaceSlug/workspace/settings/addon/configure/:appKey/:moduleKey',
      exact: false,
      component: WorkspaceSettingsConnect,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
  ];

  routes.push({
    name: ROUTE_NAME.WORKSPACE_SETTINGS_ROVO_DEV_AGENTS,
    path: '/:workspaceSlug/workspace/settings/rovo-dev-agents',
    exact: true,
    component: WorkspaceSettingsRovoDevAgents,
    layout: WorkspaceSettingsLayout,
    sideNavigation: WorkspaceSideNavigationLoadable,
    resources: [...defaultResources, jiraSitesResource],
  });

  if (isAgenticPipelinesMenuEnabled) {
    routes.push({
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_AGENTIC_PIPELINES,
      path: '/:workspaceSlug/workspace/settings/pipelines/agentic-pipelines',
      exact: true,
      component: PipelinesWorkspaceAgenticPipelinesPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources, loadAgenticPipelinesConfiguration],
    });
  }

  if (isIframeCreateRepoUIEnabled) {
    routes.push({
      name: ROUTE_NAME.WORKSPACE_IFRAME,
      path: '/:workspaceSlug/workspace/create/repository',
      component: WorkspaceIframe,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    });
  }

  routes.push(
    {
      name: ROUTE_NAME.WORKSPACE_PACKAGES,
      path: '/:workspaceSlug/workspace/packages',
      exact: true,
      component: PackagesPage,
      layout: WorkspaceLayout,
      resources: [
        ...defaultResources,
        packagesResource,
        packagesAvailabilityResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_PACKAGES_CREATE,
      path: '/:workspaceSlug/workspace/packages/create',
      exact: true,
      component: WorkspacePackagesCreate,
      layout: FocusedTaskPageLayout,
      resources: [...defaultResources, packagesAvailabilityResource],
    }
  );

  routes.push({
    name: ROUTE_NAME.WORKSPACE_SETTINGS_OAUTH,
    path: '/:workspaceSlug/workspace/settings/oauth-clients',
    exact: true,
    component: WorkspaceSettingsOAuthLoadable,
    layout: WorkspaceSettingsLayout,
    sideNavigation: WorkspaceSideNavigationLoadable,
    resources: [...defaultResources, oauthClientsResource],
  });
  routes.push(
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_FORGE_PAGE,
      path: '/:workspaceSlug/workspace/settings/forge/:appId/:moduleKey',
      exact: true,
      component: WorkspaceSettingsForge,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_FORGE_PAGE,
      path: '/:workspaceSlug/workspace/settings/forge/:appId/:envKey/:moduleKey/:modulePath*',
      exact: true,
      component: WorkspaceSettingsForge,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    }
  );

  routes.push(
    {
      name: ROUTE_NAME.WORKSPACE_GLOBAL_FORGE_PAGE,
      path: '/:workspaceSlug/workspace/forge/:appId/:envKey/:moduleKey/:modulePath*',
      exact: true,
      component: WorkspaceGlobalForge,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_PERSONAL_SETTINGS_FORGE_PAGE,
      path: '/:workspaceSlug/workspace/personal-settings/forge/:appId/:envKey/:moduleKey/:modulePath*',
      exact: true,
      component: WorkspacePersonalSettingsForge,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    }
  );

  routes.push(
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_ACCESS_TOKENS,
      path: '/:workspaceSlug/workspace/settings/access-tokens',
      exact: true,
      component: WorkspaceSettingsAccessTokensLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_VARIABLES,
      path: '/:workspaceSlug/workspace/settings/pipelines/:path(account-variables)',
      exact: true,
      component: PipelinesWorkspaceVariablesPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [
        ...defaultResources,
        loadPipelinesWorkspaceVariablesResource,
        loadThirdPartySecretsProviderResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_RUNNERS,
      path: '/:workspaceSlug/workspace/settings/pipelines/:path(account-runners)',
      exact: true,
      component: PipelinesWorkspaceRunnersPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [
        ...defaultResources,
        loadPipelinesWorkspaceRunnersResource,
        loadRunnersStepQueueResource,
        loadRunnersStepQueueStatsResource,
        loadWorkspaceLinkedResource,
        loadWorkspaceAllowanceResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_RUNNERS,
      path: '/:workspaceSlug/workspace/settings/pipelines/runners',
      exact: true,
      component: PipelinesWorkspaceRunnersPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [
        ...defaultResources,
        loadPipelinesWorkspaceRunnersResource,
        loadRunnersStepQueueResource,
        loadRunnersStepQueueStatsResource,
        loadWorkspaceLinkedResource,
        loadWorkspaceAllowanceResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_DYNAMIC_PIPELINES,
      path: '/:workspaceSlug/workspace/settings/pipelines/dynamic-pipelines',
      exact: true,
      component: PipelinesWorkspaceDynamicPipelinesPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources, loadWorkspaceDynamicPipelinesResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_PIPELINES_USAGE_DETAILS,
      path: '/:workspaceSlug/workspace/settings/pipelines/usage-details',
      exact: true,
      component: PipelinesWorkspaceUsageDetailsPageLoadable,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [
        ...defaultResources,
        loadPipelineUsageDetailsResource,
        loadAllowanceResource,
      ],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SETTINGS_IFRAME,
      path: '/:workspaceSlug/workspace/settings/:any',
      exact: false,
      component: WorkspaceIframe,
      layout: WorkspaceSettingsLayout,
      sideNavigation: WorkspaceSideNavigationLoadable,
      resources: [...defaultResources],
    },
    {
      name: ROUTE_NAME.WORKSPACE_SEARCH,
      path: '/:workspaceSlug/workspace/search',
      exact: false,
      component: WorkspaceSearchRoute,
      layout: FocusedTaskPageLayout,
      resources: [...defaultResources, workspaceSearchResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_PULL_REQUESTS,
      path: '/:workspaceSlug/workspace/pull-requests',
      exact: false,
      component: WorkspacePullRequests,
      layout: WorkspaceLayout,
      resources: [...defaultResources, workspacePullRequestsResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_PROJECTS,
      path: '/:workspaceSlug/workspace/projects',
      exact: true,
      component: WorkspaceProjects,
      layout: WorkspaceLayout,
      resources: [...defaultResources, workspaceProjectsResource],
    },
    {
      name: ROUTE_NAME.WORKSPACE_CONNECT_TAB,
      path: '/:workspaceSlug/!addon/:appKey/:moduleKey',
      component: ConnectPageLoadable,
      layout: WorkspaceLayout,
      resources: [...defaultResources],
    }
  );

  return routes;
};
